/**
 * useDomainAgentSelection — the connected domain agents (MCP servers, GET /api/domain-agents) and which
 * of them are selected for the current conversation.
 *
 * Selected agents are the ones the chat sends its queries to (the server then gives the model only
 * their tools). Nothing selected means no domain agent is active. The selection is kept
 * per conversation in localStorage, so reloading or expanding to full screen restores it.
 * Connections are discoverable but never implicitly activated.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { domainAgentsAPI } from '../services/api.js';
import type { DomainAgent } from './useDomainAgents.ts';

export type DomainAgentEntry = { agent: DomainAgent; label: string };
type ServiceDomain = { domain: string; label: string; service: string };

// v2 intentionally does not inherit legacy implicit single-agent selections.
const PREFIX = 'lyra:domainAgentSelection:v2:';
/** The signed-in user's id (from the login token), so one browser shared by two accounts never shares a selection. */
function currentUserId(): string {
  try {
    const token = window.localStorage.getItem('authToken');
    const payload = token ? JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))) : null;
    return String(payload?.id || payload?._id || payload?.userId || '');
  } catch {
    return '';
  }
}
const keyFor = (sessionId: string | null) => {
  const uid = currentUserId();
  return `${PREFIX}${uid ? `${uid}:` : ''}${sessionId || 'draft'}`;
};

function read(key: string): string[] | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : null;
  } catch {
    return null;
  }
}
function write(key: string, ids: string[]) {
  try { window.localStorage.setItem(key, JSON.stringify(ids)); } catch { /* storage unavailable — selection just isn't remembered */ }
}

export function useDomainAgentSelection(sessionId: string | null = null, { enabled = true }: { enabled?: boolean } = {}) {
  const [entries, setEntries] = useState<DomainAgentEntry[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [errors, setErrors] = useState<string[]>([]);
  const [reloadKey, setReloadKey] = useState(0);
  // null = no choice made yet (use the default once the agents are known)
  const [chosen, setChosen] = useState<string[] | null>(() => read(keyFor(sessionId)) ?? read(keyFor(null)));

  useEffect(() => {
    if (!enabled) return undefined; // the host manages the selection itself: nothing to fetch here
    let cancelled = false;
    setStatus('loading');
    const failed: string[] = [];
    domainAgentsAPI.domains()
      .then(async (r: { domains: ServiceDomain[] }) => {
        const lists = await Promise.all(
          r.domains.map((d) =>
            domainAgentsAPI.list(d.domain, { refresh: reloadKey > 0 })
              .then((res: { agents: DomainAgent[] }) => res.agents.map((agent) => ({ agent, label: d.label })))
              .catch((e: any) => {
                failed.push(`${d.label}: ${e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'unavailable'}`);
                return [] as DomainAgentEntry[];
              })
          )
        );
        if (!cancelled) { setEntries(lists.flat()); setErrors(failed); setStatus('ready'); }
      })
      .catch(() => !cancelled && setStatus('error'));
    return () => { cancelled = true; };
  }, [reloadKey, enabled]);

  // A different conversation brings its own remembered selection; a new one inherits the current choice.
  useEffect(() => {
    if (!sessionId) return;
    const stored = read(keyFor(sessionId));
    if (stored) setChosen(stored);
  }, [sessionId]);

  const persist = useCallback((ids: string[]) => {
    write(keyFor(sessionId), ids);
    write(keyFor(null), ids); // last choice seeds the next new conversation
  }, [sessionId]);

  // No implicit selection, even if exactly one domain agent is connected.
  useEffect(() => {
    if (status !== 'ready' || chosen !== null) return;
    const initial: string[] = [];
    setChosen(initial);
    persist(initial);
  }, [status, chosen, entries, persist]);

  const present = useMemo(() => new Set(entries.map((e) => e.agent.id)), [entries]);
  // A disconnected agent can linger in storage; it must never be sent or shown.
  const selected = useMemo(() => (chosen || []).filter((id) => present.has(id)), [chosen, present]);
  const selectedAgents = useMemo(
    () => entries.filter((e) => selected.includes(e.agent.id)).map((e) => e.agent),
    [entries, selected]
  );
  const available = useMemo(
    () => entries.filter((e) => !selected.includes(e.agent.id)).map((e) => e.agent),
    [entries, selected]
  );

  const setSelection = useCallback((next: string[]) => { setChosen(next); persist(next); }, [persist]);
  const toggle = useCallback((id: string) => {
    const current = chosen || [];
    setSelection(current.includes(id) ? current.filter((x) => x !== id) : [...current, id]);
  }, [chosen, setSelection]);
  const remove = useCallback((id: string) => setSelection((chosen || []).filter((x) => x !== id)), [chosen, setSelection]);
  const reload = useCallback(() => setReloadKey((n) => n + 1), []);

  return { entries, status, errors, reload, selected, selectedAgents, available, toggle, remove, isSelected: (id: string) => selected.includes(id) };
}

export type DomainAgentSelection = ReturnType<typeof useDomainAgentSelection>;
